// Direct authentication using fetch API (bypassing Supabase client)
// This approach is more reliable in Vite environments and avoids module loading issues

import { handleApiCall, UnauthorizedError } from './errors'
import { updateStoredSession, type StoredSession } from './auth-token'
import type { User, LoginRequest, LoginResponse } from '@timetable/types'

// Get environment variables
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

// Login with email and password using direct fetch
export async function loginDirect(
  credentials: LoginRequest
): Promise<LoginResponse> {
  return handleApiCall(async () => {
    // 1. Call Supabase auth endpoint directly
    const authResponse = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
      }),
    });

    if (!authResponse.ok) {
      const errorData: any = await authResponse.json();
      throw new UnauthorizedError(errorData.error_description || errorData.msg || 'Login failed')
    }

    const authData: any = await authResponse.json();

    // 2. Fetch user details from users table
    console.log('[loginDirect] Attempting to fetch user from database. User ID:', authData.user.id);
    const userResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/users?id=eq.${authData.user.id}&select=*`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${authData.access_token}`,
        },
      }
    );

    let userData: User | null = null;
    
    if (userResponse.ok) {
      const users: any = await userResponse.json();
      userData = users[0] || null;
      console.log('[loginDirect] User fetched from database:', userData);
      
      if (!userData) {
        console.error('[loginDirect] User record not found in public.users table for ID:', authData.user.id);
        console.error('[loginDirect] This user exists in auth.users but not in public.users');
        console.error('[loginDirect] Please run the seed file or manually insert the user record');
      }
    } else {
      const errorText = await userResponse.text();
      console.error('[loginDirect] Failed to fetch user from database. Status:', userResponse.status);
      console.error('[loginDirect] Error response:', errorText);
      console.error('[loginDirect] This usually means RLS policies are blocking access or the user record doesn\'t exist');
      // Don't throw error, just use fallback
    }

    // If user doesn't exist in users table, throw an error instead of using fallback
    if (!userData) {
      throw new UnauthorizedError(
        'User record not found. Please contact your administrator to set up your account.'
      );
    }

    const user: User = userData;

    return {
      user,
      session: {
        access_token: authData.access_token,
        refresh_token: authData.refresh_token,
        expires_at: authData.expires_at || 0,
      },
    }
  })
}

// Refresh the access token using the refresh token
export async function refreshSessionDirect(refreshToken: string): Promise<StoredSession> {
  const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!response.ok) {
    throw new UnauthorizedError('Refresh token expired or invalid')
  }

  const data: any = await response.json()
  const session: StoredSession = {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: data.expires_at || Math.floor(Date.now() / 1000) + 3600,
  }

  // Persist the new tokens immediately
  updateStoredSession(session)

  return session
}

// Logout using direct fetch
export async function logoutDirect(): Promise<void> {
  return handleApiCall(async () => {
    // Get current session from localStorage
    const sessionStr = typeof window !== 'undefined' ? window.localStorage.getItem('supabase.auth.token') : null;
    if (!sessionStr) return;

    try {
      const session = JSON.parse(sessionStr);
      const accessToken = session?.currentSession?.access_token;

      if (accessToken) {
        await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local storage
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('supabase.auth.token');
      }
    }
  })
}

// Get current user using direct fetch
export async function getCurrentUserDirect(): Promise<User | null> {
  return handleApiCall(async () => {
    // Get session from localStorage
    const sessionStr = typeof window !== 'undefined' ? window.localStorage.getItem('supabase.auth.token') : null;
    if (!sessionStr) return null;

    try {
      const session = JSON.parse(sessionStr);
      const accessToken = session?.currentSession?.access_token;
      const userId = session?.currentSession?.user?.id;

      if (!accessToken || !userId) return null;

      // Fetch user details
      const userResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/users?id=eq.${userId}&select=*`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!userResponse.ok) return null;

      const users: any = await userResponse.json();
      return users[0] || null;
    } catch (err) {
      console.error('Get current user error:', err);
      return null;
    }
  })
}

// Signup with School ID validation
export async function signupWithSchoolIdDirect(
  signupData: { email: string; password: string; name: string; schoolId: string }
): Promise<LoginResponse> {
  return handleApiCall(async () => {
    console.log('[signupWithSchoolIdDirect] Starting signup for:', signupData.email, 'with school:', signupData.schoolId);
    
    // 1. Validate School ID exists
    const schoolResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/schools?school_id=eq.${signupData.schoolId}&select=id`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
        },
      }
    );
    const schools = await schoolResponse.json();
    console.log('[signupWithSchoolIdDirect] School validation response:', schools);
    
    if (!schools || schools.length === 0) {
      throw new UnauthorizedError('Invalid School ID. Please check your School ID and try again.');
    }

    // 2. Create auth user via Supabase Auth
    const authResponse = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        email: signupData.email,
        password: signupData.password,
      }),
    });

    if (!authResponse.ok) {
      const errorData: any = await authResponse.json();
      console.error('[signupWithSchoolIdDirect] Auth signup failed:', errorData);
      
      // Handle rate limiting
      if (errorData.error_description?.includes('rate limit') || errorData.msg?.includes('rate limit')) {
        throw new Error('Too many signup attempts. Please wait a few minutes and try again.');
      }
      
      throw new UnauthorizedError(errorData.error_description || errorData.msg || 'Signup failed')
    }

    const authData: any = await authResponse.json();
    console.log('[signupWithSchoolIdDirect] Auth user created:', authData.user?.id);

    // 3. Create user record with school_id
    const userPayload = {
      id: authData.user.id,
      email: signupData.email,
      name: signupData.name,
      school_id: signupData.schoolId,
      role: 'standard',
      status: 'pending',
    };
    console.log('[signupWithSchoolIdDirect] Creating user record with payload:', userPayload);

    const userResponse = await fetch(`${SUPABASE_URL}/rest/v1/users`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${authData.access_token}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(userPayload),
    });

    if (!userResponse.ok) {
      const errorText = await userResponse.text();
      console.error('[signupWithSchoolIdDirect] User record creation failed. Status:', userResponse.status);
      console.error('[signupWithSchoolIdDirect] Error response:', errorText);
      
      let errorData: any;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }
      
      throw new Error(errorData.message || errorData.hint || 'Failed to create user record')
    }

    const users: any = await userResponse.json();
    console.log('[signupWithSchoolIdDirect] User record created:', users);
    
    if (!users || users.length === 0) {
      throw new Error('User record was not returned after creation');
    }
    
    const user: User = users[0];

    return {
      user,
      session: {
        access_token: authData.access_token,
        refresh_token: authData.refresh_token,
        expires_at: authData.expires_at || 0,
      },
    }
  })
}
